import { Grid, Stack, Typography } from '@mui/material'
import { KFieldLabel } from 'components/core/KFieldLabel'
import { useCurrentStore, useOrdering } from 'hooks'
import { useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { buildOrderTypeNotes } from 'services'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { OrderTypeSelect } from './OrderTypeSelect'
import { Order } from 'models'
import { OrderAsapLaterSelect } from './OrderAsapLaterSelect'
import { OrderInfoDeliveryAddressInput } from './OrderInfoDeliveryAddressInput'
import { OrderInfoTimeInput } from './OrderInfoTimeInput'

interface OrderInfoProps {
  allowChangingOrderType?: boolean
  submitForm: SubmitOrderSectionProps
}

export const OrderInfo = ({
  submitForm: { form },
  allowChangingOrderType = true,
}: OrderInfoProps) => {
  const { t } = useTranslation()
  const { order, isAsapEnabled, saveOrderProps } = useOrdering()
  const { store } = useCurrentStore()

  useEffect(() => {
    if (order && order.is_asap !== false && isAsapEnabled === false) {
      saveOrderProps({ is_asap: false })
    }
  }, [isAsapEnabled]) // eslint-disable-line react-hooks/exhaustive-deps

  const orderType = order?.order_type

  const notes = useMemo(() => {
    if (!store || !order?.order_type) return []
    return buildOrderTypeNotes(store, order?.order_type)
  }, [store, order?.order_type])

  return (
    <Stack sx={{ gap: 2 }}>
      <Grid container spacing={2.5}>
        {allowChangingOrderType && (
          <Grid
            item
            xs={12}
            sm={6}
            container
            direction="column"
            sx={{ gap: 1, justifyContent: 'flex-end' }}
          >
            <OrderTypeSelect
              value={orderType}
              onChange={(value) => {
                saveOrderProps({ order_type: value })
              }}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sm={6}
          container
          direction="column"
          sx={{ gap: 1, justifyContent: 'flex-end' }}
        >
          <KFieldLabel>{t('When would you like to order?')}</KFieldLabel>
          <OrderAsapLaterSelect
            value={order?.is_asap}
            onChange={(value) => saveOrderProps({ is_asap: value } as Order)}
          />
        </Grid>
      </Grid>

      <OrderInfoTimeInput form={form} />
      <OrderInfoDeliveryAddressInput form={form} />

      <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
        <Typography variant="h4" color="error" mb={0.5}>
          {t('Important notes:')}
        </Typography>
        {notes.map(([note, params], index) => (
          <Typography key={index} variant="body2" color="secondary">
            <Trans defaults={note} values={params} />
          </Typography>
        ))}
      </Stack>
    </Stack>
  )
}
